import React, { useEffect, useRef, useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { HeroSectionB } from '../components/HeroSectionB';
import { HeroSectionKit } from '../components/HeroSectionKit';
import { HeroSectionCards } from '../components/HeroSectionCards';
import { HeroSectionStory } from '../components/HeroSectionStory';
import { IncludesCarousel } from '../components/IncludesCarousel';
import { SignalSection } from '../components/SignalSection';
import { SymptomsSection } from '../components/SymptomsSection';
import { SignalBridge } from '../components/SignalBridge';
import { OfferSection } from '../components/OfferSection';
type DeferredProps = {
  load: () => Promise<any>;
  minHeight?: number;
  id?: string;
};
function DeferredSection({
  load,
  minHeight = 420,
  id
}: DeferredProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    const loadNow = () => {
      load().then(module => {
        if (!cancelled) {
          const exported = module.default ?? Object.values(module).find(value => typeof value === 'function');
          if (exported) setComponent(() => exported as React.ComponentType);
        }
      }).catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      loadNow();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        loadNow();
      }
    }, {
      rootMargin: '1000px 0px'
    });
    observer.observe(host);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [load]);
  return <div id={id} ref={hostRef} style={{
    minHeight: Component ? undefined : minHeight
  }} aria-busy={!Component} data-id="element-812" data-mp="1wxwrohqra2ok">
      {Component ? <Component data-id="element-813" data-mp="355st6od43rd8" /> : null}
    </div>;
}
const loadPain = () => import('../components/PainSection').then(m => ({
  default: m.PainSection
}));
const loadBenefits = () => import('../components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
}));
const loadProtocol = () => import('../components/ProtocolSection').then(m => ({
  default: m.ProtocolSection
}));
const loadQuiz = () => import('../components/QuizSection').then(m => ({
  default: m.QuizSection
}));
const loadDifference = () => import('../components/DifferenceSection').then(m => ({
  default: m.DifferenceSection
}));
const loadTestimonials = () => import('../components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
}));
const loadHowItWorks = () => import('../components/HowItWorksSection').then(m => ({
  default: m.HowItWorksSection
}));
const loadForWho = () => import('../components/ForWhoSection').then(m => ({
  default: m.ForWhoSection
}));
const loadIdentity = () => import('../components/IdentityTransitionSection').then(m => ({
  default: m.IdentityTransitionSection
}));
const loadOffer = () => import('../components/OfferSection').then(m => ({
  default: m.OfferSection
}));
const loadFAQ = () => import('../components/FAQSection').then(m => ({
  default: m.FAQSection
}));
const loadGift = () => import('../components/GiftDay1Section').then(m => ({
  default: m.GiftDay1Section
}));
const loadFinalCTA = () => import('../components/FinalCTASection').then(m => ({
  default: m.FinalCTASection
}));
export function LandingPage() {
  const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
  // Hero por defecto: cards (Protocolo H.E.R. protagonista + carrusel "Todo lo que incluye H.E.R." que se mueve
  // solo), y "Tu cuerpo no falló" como sección quieta justo debajo.
  // Variantes: ?v=story (relato animado de ~6 s antes de H.E.R.), ?v=kit (composición estática),
  // ?v=a (hero original) y ?v=b (variante B).
  // ?regalo=1 suma la sección "Probá tu Día 1 gratis" entre FAQ y el cierre (prueba; por defecto no aparece).
  const variant = params.get('v') ?? 'cards';
  const showGift = params.get('regalo') === '1';
  const previewPostPurchase = params.get('preview') === 'postpurchase';
  if (previewPostPurchase) {
    return <main className="min-h-screen bg-background text-foreground font-body" data-id="element-814" data-mp="322juayoy6zv6"><OfferSection data-id="element-815" data-mp="3207g8wmb99ne" /></main>;
  }
  const isStory = variant === 'story';
  const isTopHero = variant === 'cards' || variant === 'kit' || isStory;
  // Orden de las secciones (después del hero): Pain → Benefits → Método → Quiz → ¿En qué momento estás? (+ video)
  // → De la improvisación a una estrategia → Para quién → Identidad → Oferta → FAQ → Cierre.
  return <main className="overflow-hidden bg-background text-foreground min-h-screen font-body" data-id="element-816" data-mp="2j4hcb3uti36v">
      {isStory && <HeroSectionStory data-id="element-817" data-mp="ma31ixcik290" />}
      {isStory && <div className="bg-gradient-hero pt-8" data-id="element-818" data-mp="m91mkhsdmvm0"><IncludesCarousel data-id="element-819" data-mp="hbjfyy1u0sh6" /></div>}
      {variant === 'cards' && <HeroSectionCards data-id="element-820" data-mp="h9yh4w8hindi" />}
      {variant === 'kit' && <HeroSectionKit data-id="element-821" data-mp="ptv2ppe7kpp7" />}
      {!isStory && <SignalSection data-id="element-822" data-mp="pstgcorqsgs6" />}
      {!isStory && <SymptomsSection data-id="element-823" data-mp="tuf10waipzcx" />}
      {!isStory && <SignalBridge data-id="element-824" data-mp="ttcbaroxm2yl" />}
      {isTopHero ? null : variant === 'b' ? <HeroSectionB data-id="element-825" data-mp="ylnattfrfmqm" /> : <HeroSection data-id="element-826" data-mp="yl3qk62n0m9f" />}
      <DeferredSection load={loadPain} minHeight={420} data-id="element-827" data-mp="ymokbth4o168" />
      <DeferredSection load={loadBenefits} minHeight={360} data-id="element-828" data-mp="13k6z40httam5" />
      <DeferredSection load={loadProtocol} minHeight={620} data-id="element-829" data-mp="13jork7ppr9yx" />
      <DeferredSection load={loadQuiz} minHeight={620} data-id="element-830" data-mp="6uzcsq9duxh6" />
      <DeferredSection load={loadHowItWorks} minHeight={480} data-id="element-831" data-mp="6wk6yersceei" />
      <DeferredSection load={loadTestimonials} minHeight={480} data-id="element-832" data-mp="awkmk2qmevn5" />
      <DeferredSection load={loadDifference} minHeight={620} data-id="element-833" data-mp="fucl3flrtg0b" />
      <DeferredSection load={loadForWho} minHeight={520} data-id="element-834" data-mp="fwyqb8l6vjfd" />
      <DeferredSection load={loadIdentity} minHeight={480} data-id="element-835" data-mp="kv0e26tk86x6" />
      <DeferredSection load={loadOffer} minHeight={720} id="offer" data-id="element-836" data-mp="1rab0s2bgf0ai" />
      <DeferredSection load={loadFAQ} minHeight={560} data-id="element-837" data-mp="1rc5bknt0hc0v" />
      {showGift && <DeferredSection load={loadGift} minHeight={420} id="regalo" data-id="element-838" data-mp="1w7uen148pb1x" />}
      <DeferredSection load={loadFinalCTA} minHeight={560} data-id="element-839" data-mp="16kgupefp7sef" />
    </main>;
}